/**
 * データローダー
 *
 * UI用JSONファイルからWork/Eventデータを読み込む機能を提供します。
 *
 * @see public/data/json/works-ui.json
 * @see public/data/json/events-ui.json
 * @see docs/system.md
 */

import type { Work, Event, ChronologyEntry } from '@/types/domain';

// --- メモリキャッシュ（モジュールスコープ） ---
// 成功時のみ更新。失敗時はキャッシュを触らず次回再試行可能にする。
let worksCache: Work[] | null = null;
let eventsCache: Event[] | null = null;
let chronologyCache: ChronologyEntry[] | null = null;

// --- 最小限の構造バリデーション ---

function isValidWork(item: unknown): item is Work {
  return (
    typeof item === 'object' &&
    item !== null &&
    'id' in item &&
    typeof (item as Record<string, unknown>)['id'] === 'string'
  );
}

function isValidEvent(item: unknown): item is Event {
  return (
    typeof item === 'object' &&
    item !== null &&
    'id' in item &&
    typeof (item as Record<string, unknown>)['id'] === 'string'
  );
}

/**
 * 作品データを読み込む
 *
 * data/json/works-ui.json からWork配列を取得します。
 * キャッシュ済みの場合は再フェッチせず即座に返します。
 *
 * @returns 作品データの配列
 * @throws データの読み込みまたはパースに失敗した場合
 */
export async function loadWorksData(): Promise<Work[]> {
  if (worksCache) return worksCache;

  try {
    const response = await fetch('/data/json/works-ui.json');

    if (!response.ok) {
      throw new Error(
        `作品データの読み込みに失敗しました: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    // UI用JSONは直接配列形式
    if (!Array.isArray(data)) {
      throw new Error('作品データの形式が不正です: 配列ではありません');
    }

    worksCache = data.filter(isValidWork);
    return worksCache;
  } catch (error) {
    console.error('作品データの読み込みエラー:', error);
    throw error;
  }
}

/**
 * イベントデータを読み込む
 *
 * data/json/events-ui.json からEvent配列を取得します。
 * キャッシュ済みの場合は再フェッチせず即座に返します。
 *
 * @returns イベントデータの配列
 * @throws データの読み込みまたはパースに失敗した場合
 */
export async function loadEventsData(): Promise<Event[]> {
  if (eventsCache) return eventsCache;

  try {
    const response = await fetch('/data/json/events-ui.json');

    if (!response.ok) {
      throw new Error(
        `イベントデータの読み込みに失敗しました: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    // UI用JSONは直接配列形式
    if (!Array.isArray(data)) {
      throw new Error('イベントデータの形式が不正です: 配列ではありません');
    }

    eventsCache = data.filter(isValidEvent);
    return eventsCache;
  } catch (error) {
    console.error('イベントデータの読み込みエラー:', error);
    throw error;
  }
}

/**
 * 年表データを読み込む
 *
 * data/json/chronology.json からChronologyEntry配列を取得します。
 * キャッシュ済みの場合は再フェッチせず即座に返します。
 *
 * @returns 年表データの配列
 * @throws データの読み込みまたはパースに失敗した場合
 */
export async function loadChronologyData(): Promise<ChronologyEntry[]> {
  if (chronologyCache) return chronologyCache;

  try {
    const response = await fetch('/data/json/chronology.json');

    if (!response.ok) {
      throw new Error(
        `年表データの読み込みに失敗しました: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    // データの妥当性チェック
    const chronologyArray = data.chronology || data;
    if (!Array.isArray(chronologyArray)) {
      throw new Error('年表データの形式が不正です: 配列ではありません');
    }

    chronologyCache = chronologyArray as ChronologyEntry[];
    return chronologyCache;
  } catch (error) {
    console.error('年表データの読み込みエラー:', error);
    throw error;
  }
}

/**
 * すべてのデータを一括で読み込む
 *
 * @returns 作品とイベントのデータ
 * @throws データの読み込みに失敗した場合
 *
 * @example
 * ```typescript
 * const { works, events } = await loadAllData();
 * console.log(`作品: ${works.length}件、イベント: ${events.length}件`);
 * ```
 */
export async function loadAllData(): Promise<{
  works: Work[];
  events: Event[];
}> {
  const [works, events] = await Promise.all([
    loadWorksData(),
    loadEventsData(),
  ]);

  return { works, events };
}

/**
 * IDから作品を検索
 *
 * @param works - 作品データ配列
 * @param id - 検索する作品のID（例: DB_000001）
 * @returns 見つかった作品、見つからない場合はundefined
 *
 * @example
 * ```typescript
 * const work = findWorkById(works, 'DB_000001');
 * if (work) {
 *   console.log(work.title.ja);
 * }
 * ```
 */
export function findWorkById(works: Work[], id: string): Work | undefined {
  return works.find((work) => work.id === id);
}

/**
 * IDからイベントを検索
 *
 * @param events - イベントデータ配列
 * @param id - 検索するイベントのID（例: 00001）
 * @returns 見つかったイベント、見つからない場合はundefined
 *
 * @example
 * ```typescript
 * const event = findEventById(events, '00001');
 * if (event) {
 *   console.log(event.title.ja);
 * }
 * ```
 */
export function findEventById(events: Event[], id: string): Event | undefined {
  return events.find((event) => event.id === id);
}

/**
 * URLパスから作品を検索
 *
 * URL例: /works/DB_000001 → id が 'DB_000001' のものを検索
 *
 * @param works - 作品データ配列
 * @param slug - URLスラッグ（idと同じ）
 * @returns 見つかった作品、見つからない場合はundefined
 *
 * @example
 * ```typescript
 * // URL: /works/DB_000001
 * const work = findWorkBySlug(works, 'DB_000001');
 * ```
 */
export function findWorkBySlug(works: Work[], slug: string): Work | undefined {
  return works.find((work) => work.id === slug);
}

/**
 * URLパスからイベントを検索
 *
 * URL例: /events/00001 → id が '00001' のものを検索
 *
 * @param events - イベントデータ配列
 * @param slug - URLスラッグ（idと同じ）
 * @returns 見つかったイベント、見つからない場合はundefined
 *
 * @example
 * ```typescript
 * // URL: /events/00001
 * const event = findEventBySlug(events, '00001');
 * ```
 */
export function findEventBySlug(
  events: Event[],
  slug: string
): Event | undefined {
  return events.find((event) => event.id === slug);
}

/**
 * IDからURLスラッグを生成
 *
 * UI用JSONではidがそのままslugとして使用される
 *
 * @param id - エンティティのid
 * @returns URLスラッグ（idをそのまま返す）
 *
 * @example
 * ```typescript
 * generateSlug('DB_000001');
 * // => 'DB_000001'
 * ```
 */
export function generateSlug(id: string): string {
  return id;
}

// エイリアス（後方互換性のため）
// NOTE: 新規コードでは data-store 経由の fetchWorks / fetchEvents を推奨
export const loadWorks = loadWorksData;
export const loadEvents = loadEventsData;
export const loadChronology = loadChronologyData;
